import React from 'react';
import styled from 'styled-components';

const Wrap = styled.div`
  position: fixed;
  right: 18px;
  bottom: 88px;
  display: grid;
  gap: 10px;
  z-index: 40;
  pointer-events: none; /* клики проходят, пока не на кнопках */
`;

const GlassBtn = styled.button`
  pointer-events: auto;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  border: 1px solid var(--bdr);
  background: var(--card);
  box-shadow: var(--shadow);
  display: grid;
  place-items: center;
  position: relative;
  cursor: pointer;
  transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;

  &:hover { transform: translateY(-1px); }
  &:active { transform: translateY(0); }

  /* иконка-стрелка */
  svg.arrow {
    width: 18px;
    height: 18px;
    opacity: .9;
  }

  /* кольцевой прогресс */
  svg.ring {
    position: absolute;
    inset: 0;
    width: 48px;
    height: 48px;
    transform: rotate(-90deg);
  }

  /* тултип */
  .tip {
    position: absolute;
    right: calc(100% + 10px);
    top: 50%;
    transform: translateY(-50%);
    background: var(--card);
    border: 1px solid var(--bdr);
    box-shadow: var(--shadow);
    color: inherit;
    padding: 6px 8px;
    border-radius: 10px;
    font-size: 12px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity .18s ease;
  }

  &:hover .tip { opacity: 1; }
`;

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

export default function ScrollButtons() {
  const [visible, setVisible] = React.useState(false);
  const [progress, setProgress] = React.useState(0); // 0..1
  const holdTimerRef = React.useRef(null);

  const compute = React.useCallback(() => {
    const doc = document.documentElement;
    const scrollTop = doc.scrollTop || document.body.scrollTop;
    const max = doc.scrollHeight - doc.clientHeight;
    const p = max > 0 ? clamp(scrollTop / max, 0, 1) : 0;
    setProgress(p);
    setVisible(scrollTop > 200);
  }, []);

  React.useEffect(() => {
    compute();
    const onScroll = () => compute();
    const onResize = () => compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [compute]);

  const toTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const toBottom = () =>
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });

  // удержание кнопки — непрерывная прокрутка
  const startHold = (dir = 'up') => {
    stopHold();
    const step = () => {
      const delta = dir === 'up' ? -60 : 60;
      window.scrollBy({ top: delta, left: 0 });
      holdTimerRef.current = requestAnimationFrame(step);
    };
    holdTimerRef.current = requestAnimationFrame(step);
  };
  const stopHold = () => {
    if (holdTimerRef.current) cancelAnimationFrame(holdTimerRef.current);
    holdTimerRef.current = null;
  };

  if (!visible) return null;

  // параметры кольца прогресса
  const R = 22; // радиус
  const C = 2 * Math.PI * R;
  const dash = C * progress;

  return (
    <Wrap>
      {/* UP */}
      <GlassBtn
        aria-label="Back to top"
        onClick={toTop}
        onMouseDown={() => startHold('up')}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
      >
        <svg className="ring" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r={R} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="2" />
          <circle
            cx="24" cy="24" r={R} fill="none"
            stroke="var(--primary)" strokeWidth="3" strokeLinecap="round"
            strokeDasharray={`${dash} ${C - dash}`}
          />
        </svg>
        <svg className="arrow" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 5l-7 7h4v7h6v-7h4z" fill="currentColor" />
        </svg>
        <span className="tip">Back to top</span>
      </GlassBtn>

      {/* DOWN */}
      <GlassBtn
        aria-label="To bottom"
        onClick={toBottom}
        onMouseDown={() => startHold('down')}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
      >
        <svg className="ring" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r={R} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="2" />
          <circle
            cx="24" cy="24" r={R} fill="none"
            stroke="var(--primary)" strokeWidth="3" strokeLinecap="round"
            strokeDasharray={`${C - dash} ${dash}`}
          />
        </svg>
        <svg className="arrow" viewBox="0 0 24 24" aria-hidden="true" style={{ transform: 'rotate(180deg)' }}>
          <path d="M12 5l-7 7h4v7h6v-7h4z" fill="currentColor" />
        </svg>
        <span className="tip">To bottom</span>
      </GlassBtn>
    </Wrap>
  );
}
